import React from 'react';
import styled from '@emotion/styled';
import { isEqual } from 'lodash';
import { Waypoint } from 'react-waypoint';
import { selectCurrentPath, selectFolder, selectNextPages } from '@/redux/selectors/fileManagerSelectors';
import { selectFileQueue } from '@/redux/selectors/fileQueueSelectors';
import { CLEAN_PATH_REGEX, clearForwardLocations, getFolder, getMoreFiles } from '@/redux/slices/fileManagerSlice';
import { List, ListItemButton, ListItemIcon } from '@mui/material';
import FileItemText from '../FileItem/FileItemText';
import FolderIcon from '../FileItem/FolderIcon';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import FileItemIcon from '../FileItem/FileItemIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

interface Props {
  depth: number;
}

const FileNavigationColumn: React.FC<Props> = ({ depth }) => {
  const dispatch = useAppDispatch();
  const items: FileManagerInventoryFile[] = useAppSelector((state) => selectFolder(state, depth));
  const currentPath = useAppSelector(selectCurrentPath);
  const nextPages = useAppSelector(selectNextPages);
  const fileQueue = useAppSelector(selectFileQueue);

  const handleItemClick = (item: FileManagerInventoryFile) => {
    const cleanItemName = item.name.replace(CLEAN_PATH_REGEX, '');
    const path = currentPath.slice(0, depth + 1).concat(cleanItemName);
    if (item.ptype !== 'FOLDER') {
      path.pop();
    }
    if (!isEqual(currentPath, path)) {
      dispatch(clearForwardLocations());
      dispatch(getFolder({ path }));
    }
  };

  const loadNextPage = () => {
    dispatch(getMoreFiles({ depth }));
  };

  const filteredItems = items.filter((item: FileManagerInventoryFile) => {
    const cleanName = item.name.replace(/(^[/]+)|([/]+$)/g, '');
    return cleanName !== '';
  });

  return (
    <List component="nav" aria-label="folders files list">
      {filteredItems.map((item: FileManagerInventoryFile, i) => {
        const cleanItemName = item.name.replace(/(^[/]+)|([/]+$)/g, '');
        const isFolder = item.ptype === 'FOLDER';
        const selected = isFolder && currentPath[depth + 1] === cleanItemName;
        const inFileQueue = !!fileQueue.find((file: FileManagerInventoryFile) => file.key === item.key);

        return (
          <ListItemButton
            key={`${item.origin_name}_${item.key}`}
            selected={selected}
            onClick={() => handleItemClick(item)}
            style={{ borderRadius: 0, opacity: !selected ? 0.66 : 1 }}
          >
            <StyledListItemIcon>
              {isFolder ? <FolderIcon open={selected} /> : <FileItemIcon hover={false} selected={inFileQueue} />}
            </StyledListItemIcon>

            <FileItemText name={`${item.name}${isFolder && !item.name.endsWith('/') ? '/' : ''}`} />
          </ListItemButton>
        );
      })}
      {nextPages[depth] && <Waypoint onEnter={loadNextPage} />}
    </List>
  );
};

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: 1rem;
  opacity: 0.6;
  margin-right: ${({ theme }) => theme.spacing(1.5)};
  &:hover {
    cursor: pointer;
  }
`;

export default FileNavigationColumn;
